/**
 * useVersionCheck
 *
 * Solo activo en PRODUCCIÓN (process.env.NODE_ENV === 'production').
 * En desarrollo local no hace nada para evitar loops de reload.
 *
 * Compara la versión actual (BUILD_TIME embebido al compilar)
 * contra /version.json en el servidor cada `intervalMinutes` minutos.
 * Si detecta una versión nueva, fuerza un hard-reload.
 */
import { useEffect } from 'react';
import { isGameActive } from '../lib/gameActivity';

const IS_DEV = process.env.NODE_ENV === 'development';
const CURRENT_VERSION = process.env.REACT_APP_BUILD_TIME || 'dev';

export function useVersionCheck(intervalMinutes = 5) {
  useEffect(() => {
    // Never run in local development — avoids infinite reload loop
    if (IS_DEV) return;

    let timer;
    let pendingVersion = false;
    let cancelled = false;
    const reloadWhenSafe = () => {
      if (pendingVersion && !cancelled && !isGameActive()) {
        pendingVersion = false;
        window.location.reload();
      }
    };
    window.addEventListener('aorra:game-activity', reloadWhenSafe);

    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json', {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (!res.ok) return;

        const data = await res.json();
        const serverVersion = data.version;

        // Skip if server still has the placeholder (build not yet run)
        if (!serverVersion || serverVersion === 'BUILD_PLACEHOLDER') return;
        // Skip if current version is also dev/placeholder
        if (CURRENT_VERSION === 'dev' || CURRENT_VERSION === 'BUILD_PLACEHOLDER') return;

        if (serverVersion !== CURRENT_VERSION) {
          pendingVersion = true;
          reloadWhenSafe();
        }
      } catch {
        // Error de red — ignorar, reintenta en el próximo intervalo
      }
    };

    checkVersion();
    timer = setInterval(checkVersion, intervalMinutes * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('aorra:game-activity', reloadWhenSafe);
    };
  }, [intervalMinutes]);
}
